import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';

import { concatWithDot } from 'helpers/string';

import { IFundingDirections } from './types';
import { EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES, prefix } from '../../utils/form';

export const FundingDirections: React.FC<IFundingDirections> = ({ disabled, totalCost, isTotalCostMatch }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <Grid container spacing={{ xs: 2, md: 3 }} sx={{ alignItems: 'flex-end' }}>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.researchCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.RESEARCH_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.researchCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.researchCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.RESEARCH_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.marketingCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.MARKETING_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.marketingCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.marketingCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.MARKETING_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.workingCapitalCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.WORKING_CAPITAL_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.workingCapitalCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.workingCapitalCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.WORKING_CAPITAL_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.equipmentCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.EQUIPMENT_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.equipmentCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.equipmentCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.EQUIPMENT_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.constructionCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.CONSTRUCTION_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.constructionCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.constructionCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.CONSTRUCTION_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.otherCost.currencyAmount"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.OTHER_COST)}.currencyAmount`}
          label={t('executiveSummary.financialNeeds.otherCost')}
          adornmentType="currency"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.otherCost.percentage"
          name={`${concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.OTHER_COST)}.percentage`}
          adornmentType="percentage"
          disabled={disabled}
          readOnly
          decimalScale={1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.total.amount"
          name="executiveSummary.financialNeeds.total.amount"
          label={t('total')}
          value={totalCost}
          adornmentType="currency"
          status={isTotalCostMatch ? 'success' : 'error'}
          disabled={disabled}
          readOnly
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <NumberInputControlled
          control={control}
          id="executiveSummary.financialNeeds.total.percentage"
          name="executiveSummary.financialNeeds.total.percentage"
          value={totalCost > 0 ? '100' : ''}
          adornmentType="percentage"
          status={isTotalCostMatch ? 'success' : 'error'}
          disabled={disabled}
          readOnly
        />
      </Grid>
    </Grid>
  );
};
